import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Edit2, Mail, Save, User, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { getCohorts, getCurrentAssignments, getUserInfo, upsertUserProfile } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import '../styles/MyPage.css';

const sexDisplay = {
  male: '男性',
  female: '女性',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ja-JP');
  } catch {
    return iso;
  }
};

function MyPage() {
  const { user } = useAuth();
  const [dbUserInfo, setDbUserInfo] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedSex, setEditedSex] = useState('');
  const [editedCohortId, setEditedCohortId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const userEmail = user?.email || '';
  const registrationDate = useMemo(() => formatDate(user?.created_at), [user?.created_at]);

  useEffect(() => {
    const run = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const [assignmentsData, dbUserData, cohortsData] = await Promise.all([
          getCurrentAssignments().catch(() => null),
          getUserInfo().catch(() => null),
          getCohorts().catch(() => []),
        ]);
        setAssignments(assignmentsData);
        setDbUserInfo(dbUserData);
        setCohorts(cohortsData || []);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [user]);

  useEffect(() => {
    if (!dbUserInfo) return;
    setEditedName(dbUserInfo.username || '');
    setEditedSex(dbUserInfo.sex || '');
    setEditedCohortId(dbUserInfo.cohort_id != null ? String(dbUserInfo.cohort_id) : '');
  }, [dbUserInfo]);

  const cohortLabel = useMemo(() => {
    const id = dbUserInfo?.cohort_id;
    const found = cohorts.find((c) => c.id === id);
    return found ? `${found.name} (${found.code})` : '';
  }, [cohorts, dbUserInfo?.cohort_id]);

  const roundRobinRows = useMemo(() => {
    const rankings = Array.isArray(dbUserInfo?.round_robin_rankings) ? dbUserInfo.round_robin_rankings : [];
    const padded = [...rankings];
    while (padded.length < 7) padded.push(0);
    return padded.slice(0, 7).map((rank, index) => ({ label: `第${index + 1}クール`, rank }));
  }, [dbUserInfo?.round_robin_rankings]);

  const handleSave = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const cohortIdNum = editedCohortId ? Number(editedCohortId) : null;

      await upsertUserProfile({
        username: editedName,
        email: userEmail,
        sex: editedSex,
        cohort_id: cohortIdNum,
      });

      if (supabase) {
        try {
          await supabase.auth.updateUser({
            data: {
              username: editedName,
              sex: editedSex,
            },
          });
        } catch {
          // ignore
        }
      }

      const refreshed = await getUserInfo().catch(() => null);
      if (refreshed) setDbUserInfo(refreshed);
      setSuccessMessage('ユーザー情報を更新しました。');
      setEditing(false);
    } catch (e) {
      setErrorMessage(e?.message || 'ユーザー情報の更新に失敗しました。');
    }
  };

  const handleCancel = () => {
    setErrorMessage('');
    setSuccessMessage('');
    setEditedName(dbUserInfo?.username || '');
    setEditedSex(dbUserInfo?.sex || '');
    setEditedCohortId(dbUserInfo?.cohort_id != null ? String(dbUserInfo.cohort_id) : '');
    setEditing(false);
  };

  if (loading) return <LoadingSpinner text="読み込み中..." />;
  if (!user) return <LoadingSpinner text="ログインが必要です..." />;

  return (
    <div className="my-page-page">
      <div className="my-page-background">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="my-page-container">
        <header className="my-page-header">
          <div className="my-page-header-content">
            <h1 className="my-page-title">マイページ</h1>
            <p className="my-page-subtitle">登録情報の確認・変更ができます。</p>
          </div>
          <div className="my-page-header-actions">
            {dbUserInfo?.is_admin && (
              <Link to="/admin-dashboard" className="my-page-header-link is-admin">
                管理者ダッシュボード
              </Link>
            )}
            <Link to="/dashboard" className="my-page-header-link">
              ダッシュボードへ
            </Link>
          </div>
        </header>

        {errorMessage && (
          <div className="my-page-alert is-error" role="alert">
            <AlertCircle size={18} />
            <div>{errorMessage}</div>
          </div>
        )}

        {successMessage && (
          <div className="my-page-alert is-success" role="status">
            <CheckCircle2 size={18} />
            <div>{successMessage}</div>
          </div>
        )}

        <div className="my-page-grid">
          <section className="my-page-card">
            <div className="my-page-card-header">
              <h2 className="my-page-card-title">
                <User className="my-page-title-icon" size={18} />
                登録情報
              </h2>
              <div className="my-page-card-actions">
                {!editing ? (
                  <button
                    type="button"
                    className="my-page-action-button"
                    onClick={() => {
                      setErrorMessage('');
                      setSuccessMessage('');
                      setEditing(true);
                    }}
                  >
                    <Edit2 size={16} />
                    編集
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="my-page-action-button is-primary"
                      onClick={handleSave}
                      disabled={!editedName || !editedSex}
                    >
                      <Save size={16} />
                      保存
                    </button>
                    <button type="button" className="my-page-action-button" onClick={handleCancel}>
                      <X size={16} />
                      キャンセル
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="my-page-card-body">
              <div className="my-page-info-grid">
                <div className="my-page-info-item is-full">
                  <div className="my-page-info-label">メールアドレス</div>
                  <div className="my-page-info-value is-mono">
                    <Mail className="my-page-inline-icon" size={16} />
                    {userEmail}
                  </div>
                </div>

                <div className="my-page-info-item">
                  <div className="my-page-info-label">氏名</div>
                  {!editing ? (
                    <div className="my-page-info-value">{dbUserInfo?.username || ''}</div>
                  ) : (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value.replace(/\s/g, ''))}
                      className="my-page-edit-input"
                      autoComplete="name"
                    />
                  )}
                </div>

                <div className="my-page-info-item">
                  <div className="my-page-info-label">性別</div>
                  {!editing ? (
                    <div className="my-page-info-value">{sexDisplay[dbUserInfo?.sex] || ''}</div>
                  ) : (
                    <select value={editedSex} onChange={(e) => setEditedSex(e.target.value)} className="my-page-edit-select">
                      <option value="">選択してください</option>
                      <option value="male">男性</option>
                      <option value="female">女性</option>
                    </select>
                  )}
                </div>

                <div className="my-page-info-item">
                  <div className="my-page-info-label">コホート</div>
                  {!editing ? (
                    <div className="my-page-info-value">{cohortLabel || '未設定'}</div>
                  ) : (
                    <select
                      value={editedCohortId}
                      onChange={(e) => setEditedCohortId(e.target.value)}
                      className="my-page-edit-select"
                    >
                      <option value="">未設定</option>
                      {cohorts.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name} ({c.code})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="my-page-info-item is-full">
                  <div className="my-page-info-label">登録日時</div>
                  <div className="my-page-info-value is-mono">{registrationDate}</div>
                </div>
              </div>
            </div>
          </section>

          {dbUserInfo && (
            <section className="my-page-card">
              <div className="my-page-card-header">
                <h2 className="my-page-card-title">ラウンドロビン順位</h2>
              </div>
              <div className="my-page-card-body">
                <div className="my-page-rr-grid">
                  {roundRobinRows.map((row) => (
                    <div key={row.label} className="my-page-rr-item">
                      <div className="my-page-rr-label">{row.label}</div>
                      <div className="my-page-rr-value">{row.rank}位</div>
                    </div>
                  ))}
                  <div className="my-page-rr-item is-rural">
                    <div className="my-page-rr-label">地域医療</div>
                    <div className="my-page-rr-value">{dbUserInfo?.rural_ranking ?? 0}位</div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="my-page-card is-full">
            <div className="my-page-card-header">
              <h2 className="my-page-card-title">配属情報</h2>
            </div>
            <div className="my-page-card-body">
              {assignments && assignments.length > 0 ? (
                <div className="my-page-table-wrapper">
                  <table className="my-page-data-table">
                    <thead>
                      <tr>
                        <th>ターム</th>
                        <th>病院</th>
                        <th>科</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map((a, idx) => {
                        const keyParts = [
                          a?.term_id ?? 'term',
                          a?.assignment_stage ?? 'stage',
                          a?.lottery_model_id ?? 'lm',
                          a?.institution_name ?? 'inst',
                          a?.department_name ?? 'dept',
                        ];
                        const rowKey = `${keyParts.join('-')}-${idx}`;
                        return (
                          <tr key={rowKey}>
                            <td>{a.term_id}</td>
                            <td>{a.institution_name}</td>
                            <td>{a.department_name}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="my-page-empty-state">
                  <div className="my-page-empty-text">未割り当てです</div>
                  <p className="my-page-empty-subtext">抽選結果が出るまでお待ちください。</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default MyPage;
